import { useEffect, useMemo, useState } from "react";
import { api, currency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "@/components/ui/table";
import { Tag, Plus, Pencil, Power, PowerOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEAL_TYPE_LABELS = {
  bundle_fixed: "Bundle Fixed Price (any eligible)",
  product_bundle: "Product-Specific Bundle",
  category_bundle: "Category Deal",
  percent_off: "Percentage Discount",
  fixed_off: "Fixed Amount Discount",
  buy_x_get_y: "Buy X Get Y",
};

const empty = {
  name: "",
  description: "",
  deal_type: "bundle_fixed",
  eligible_product_ids: [],
  eligible_categories: [],
  required_quantity: 3,
  deal_price: 100,
  discount_percentage: null,
  discount_amount: null,
  buy_quantity: null,
  get_quantity: null,
  free_product_id: null,
  start_at: "",
  end_at: "",
  active: true,
  priority: 0,
  stackable: false,
};

function toIsoDT(local) { return local ? new Date(local).toISOString() : null; }
function toLocalDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StatusBadge({ deal }) {
  if (!deal.active) return <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Inactive</Badge>;
  const now = new Date();
  if (deal.end_at && new Date(deal.end_at) < now) return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Expired</Badge>;
  if (deal.start_at && new Date(deal.start_at) > now) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Scheduled</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>;
}

export default function Deals() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], units: [] });
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/deals").then(r => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/products/meta").then(r => setMeta(r.data));
    api.get("/products").then(r => setProducts(r.data.filter(p => p.active)));
  }, []);

  const openNew = () => setEditing({ ...empty });
  const openEdit = (d) => setEditing({
    ...empty, ...d,
    start_at: toLocalDT(d.start_at),
    end_at: toLocalDT(d.end_at),
  });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Deal name required");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        start_at: editing.start_at ? toIsoDT(editing.start_at) : null,
        end_at: editing.end_at ? toIsoDT(editing.end_at) : null,
      };
      if (editing.id) {
        const { id, vendor_id, created_at, updated_at, created_by_id, created_by_name, ...body } = payload;
        await api.patch(`/deals/${id}`, body);
        toast.success("Deal updated");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/deals/${d.id}`, { active: !d.active });
      toast.success(!d.active ? "Deal activated" : "Deal deactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Deactivate deal "${d.name}"?`)) return;
    try {
      await api.delete(`/deals/${d.id}`);
      toast.success("Deactivated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const dt = editing?.deal_type;
  const showQtyPrice = dt === "bundle_fixed" || dt === "product_bundle" || dt === "category_bundle";
  const showPct = dt === "percent_off";
  const showAmount = dt === "fixed_off";
  const showBXGY = dt === "buy_x_get_y";
  const scopeIsProducts = dt === "product_bundle" || dt === "buy_x_get_y";
  const scopeIsCategories = dt === "category_bundle";

  const toggleProduct = (pid) => {
    const arr = editing.eligible_product_ids || [];
    setEditing({ ...editing, eligible_product_ids: arr.includes(pid) ? arr.filter(x => x !== pid) : [...arr, pid] });
  };
  const toggleCategory = (c) => {
    const arr = editing.eligible_categories || [];
    setEditing({ ...editing, eligible_categories: arr.includes(c) ? arr.filter(x => x !== c) : [...arr, c] });
  };

  const summary = useMemo(() => {
    return {
      total: rows.length,
      active: rows.filter(r => r.active).length,
    };
  }, [rows]);

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <Tag className="w-3 h-3" strokeWidth={1.5} /> Specials & Deals
          </div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="deals-title">Deals Management</h1>
          <p className="text-muted-foreground mt-2 text-sm">Create and control temporary promotions. Permanent product prices are never altered.</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-primary/10 text-primary" data-testid="deals-count">{summary.active} active / {summary.total}</Badge>
          <Button data-testid="new-deal-btn" onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New Deal
          </Button>
        </div>
      </div>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Rule</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(d => (
                <TableRow key={d.id} data-testid={`deal-row-${d.id}`} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{d.description}</div>
                  </TableCell>
                  <TableCell className="text-xs">{DEAL_TYPE_LABELS[d.deal_type] || d.deal_type}</TableCell>
                  <TableCell className="text-xs font-mono">
                    {d.deal_price ? `${d.required_quantity}× → ${currency(d.deal_price)}` : ""}
                    {d.discount_percentage ? `${d.discount_percentage}% off` : ""}
                    {d.discount_amount ? `${currency(d.discount_amount)} off` : ""}
                    {(d.buy_quantity && d.get_quantity) ? `Buy ${d.buy_quantity}, get ${d.get_quantity} free` : ""}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.start_at ? new Date(d.start_at).toLocaleDateString("en-ZA") : "—"}
                    {" → "}
                    {d.end_at ? new Date(d.end_at).toLocaleDateString("en-ZA") : "—"}
                  </TableCell>
                  <TableCell className="text-xs">{d.priority || 0}</TableCell>
                  <TableCell><StatusBadge deal={d} /></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(d)} data-testid={`toggle-${d.id}`}>
                      {d.active
                        ? <PowerOff className="w-3.5 h-3.5" strokeWidth={1.5} />
                        : <Power className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-${d.id}`}>
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-[#C05746]" onClick={() => remove(d)} data-testid={`delete-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No deals yet. Create your first promotion.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] md:max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Edit deal" : "New deal"}
            </DialogTitle>
            <DialogDescription>Promotions run on top of the base price. Product prices are never modified.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Name">
                <Input data-testid="deal-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </F>
              <F label="Deal Type">
                <Select value={editing.deal_type} onValueChange={(v) => setEditing({ ...editing, deal_type: v })}>
                  <SelectTrigger data-testid="deal-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DEAL_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <div className="md:col-span-2">
                <F label="Description">
                  <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                </F>
              </div>
              {showQtyPrice && (<>
                <F label="Required Quantity">
                  <Input type="number" data-testid="deal-req-qty" value={editing.required_quantity ?? ""} onChange={(e) => setEditing({ ...editing, required_quantity: Number(e.target.value) })} />
                </F>
                <F label="Deal Price (R)">
                  <Input type="number" step="0.01" data-testid="deal-price" value={editing.deal_price ?? ""} onChange={(e) => setEditing({ ...editing, deal_price: Number(e.target.value) })} />
                </F>
              </>)}
              {showPct && (
                <F label="Discount %">
                  <Input type="number" step="0.1" data-testid="deal-pct" value={editing.discount_percentage ?? ""} onChange={(e) => setEditing({ ...editing, discount_percentage: Number(e.target.value) })} />
                </F>
              )}
              {showAmount && (
                <F label="Discount amount (R)">
                  <Input type="number" step="0.01" data-testid="deal-amount" value={editing.discount_amount ?? ""} onChange={(e) => setEditing({ ...editing, discount_amount: Number(e.target.value) })} />
                </F>
              )}
              {showBXGY && (<>
                <F label="Buy quantity">
                  <Input type="number" value={editing.buy_quantity ?? ""} onChange={(e) => setEditing({ ...editing, buy_quantity: Number(e.target.value) })} />
                </F>
                <F label="Get quantity (free)">
                  <Input type="number" value={editing.get_quantity ?? ""} onChange={(e) => setEditing({ ...editing, get_quantity: Number(e.target.value) })} />
                </F>
              </>)}
              <F label="Starts">
                <Input type="datetime-local" data-testid="deal-start" value={editing.start_at || ""} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} />
              </F>
              <F label="Ends">
                <Input type="datetime-local" data-testid="deal-end" value={editing.end_at || ""} onChange={(e) => setEditing({ ...editing, end_at: e.target.value })} />
              </F>
              <F label="Priority (higher wins)">
                <Input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: Number(e.target.value) })} />
              </F>
              <div className="flex items-center gap-2 pt-6">
                <Switch id="deal-active" checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="deal-active" />
                <Label htmlFor="deal-active" className="cursor-pointer text-sm">Active</Label>
                <span className="mx-3" />
                <Switch id="deal-stackable" checked={editing.stackable} onCheckedChange={(v) => setEditing({ ...editing, stackable: v })} data-testid="deal-stackable" />
                <Label htmlFor="deal-stackable" className="cursor-pointer text-sm">Stackable</Label>
              </div>

              <div className="md:col-span-2">
                {(scopeIsCategories || dt === "bundle_fixed" || dt === "percent_off" || dt === "fixed_off") && (
                  <F label="Eligible Categories (pick any)">
                    <div className="flex flex-wrap gap-2">
                      {meta.categories.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => toggleCategory(c)}
                          data-testid={`deal-cat-${c}`}
                          className={`px-3 py-1 rounded-full border text-xs ${
                            (editing.eligible_categories || []).includes(c)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-white text-foreground border-border hover:border-primary/40"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </F>
                )}
                {(scopeIsProducts || dt === "buy_x_get_y") && (
                  <F label="Eligible Products (pick specific SKUs)">
                    <div className="max-h-40 overflow-y-auto thin-scroll border rounded-md p-2 space-y-1">
                      {products.map(p => (
                        <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted rounded px-2 py-1">
                          <input
                            type="checkbox"
                            checked={(editing.eligible_product_ids || []).includes(p.id)}
                            onChange={() => toggleProduct(p.id)}
                            data-testid={`deal-prod-${p.sku}`}
                          />
                          <span className="font-mono text-muted-foreground">{p.sku}</span>
                          <span>{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </F>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={saving} data-testid="save-deal-btn" onClick={save} className="rounded-full bg-primary hover:bg-primary/90">Save deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
